import { HttpEventType } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SafeUrl } from '@angular/platform-browser';
import { CassError } from '@core/models/cass-error';
import { Logger } from '@core/services/logger.service';
import { OrdersService } from '@core/services/orders.service';
import { MaterialPreviewDialogParams } from '@order/models/material-preview-dialog-params';
import { MaterialPreviewDialogResult } from '@order/models/material-preview-dialog-result';
import { FindMaterialDialogComponent } from '@order/order/find-material-dialog/find-material-dialog.component';
import { OrderHelperService } from '@order/services/order-helper.service';
import { OrderStateService } from '@order/services/order-state.service';
import { UserAlertService } from '@shared/services/user-alert.service';

@Component({
	selector: 'cass-material-preview-dialog',
	templateUrl: './material-preview-dialog.component.html',
	styleUrls: ['./material-preview-dialog.component.css']
})
export class MaterialPreviewDialogComponent {

	private readonly COMPONENT_NAME = "MaterialPreviewDialogComponent";

	protected readonly materialPreview!: MaterialPreviewDialogParams;

	imagePreviewUrl: SafeUrl = '';
	isUploading: boolean = false;
	isProcessing: boolean = false;
	fileUploadProgress: number | undefined;

	constructor (
		@Inject(MAT_DIALOG_DATA) private _materialPreview: MaterialPreviewDialogParams,
		private _alertService: UserAlertService,
		private _dialogRef: MatDialogRef<MaterialPreviewDialogComponent>,
		private _logger: Logger,
		private _matDialog: MatDialog,
		private _orderHelperService: OrderHelperService,
		private _orderService: OrdersService,
		private _orderStateService: OrderStateService
	) {
		if (_materialPreview) {
			this.materialPreview = _materialPreview;
			this.loadPreview();
		}
	}

	private loadPreview(materialId?: string) {
		materialId = materialId || this.materialPreview?.orderAdDetail.materialId;
		if (!materialId) {
			return;
		}

		this.isUploading = true;
		this._orderService.getMaterialPreview(this.materialPreview?.orderId, materialId)
			.subscribe({
				next: (res) => {
					if (res) {
						const reader = new FileReader();
						reader.onloadend = () => this.imagePreviewUrl = reader.result as string;
						reader.readAsDataURL(res);
						this.isUploading = false;
					}
				},
				error: () => {
					this.isUploading = false;
					this._logger.error(this.COMPONENT_NAME, `Cannot load material preview, orderId="${this.materialPreview?.orderId}", materialId="${materialId}"`);
				}
			});
	}

	closeDialog(materialPreviewDialogResult: MaterialPreviewDialogResult | undefined = undefined) {
		this._dialogRef.close(materialPreviewDialogResult);
	}

	uploadMaterial(file: File) {
		// Initialize file upload progress
		this.fileUploadProgress = 0;

		// Initialize material id
		const materialId = this.materialPreview?.orderAdDetail.materialId;

		// Call Api to upload material
		const uploadFile$ = this._orderService.saveOrderMaterial(file, this.materialPreview.orderId, materialId);

		// Listen for upload progress
		uploadFile$.subscribe({
			next: (event) => {
				if (event.type == HttpEventType.UploadProgress) {
					this.fileUploadProgress = Math.round(100 * (event.loaded / (event.total || 1)));

					if (event.loaded > 0 && event.loaded === event.total) {
						// the file is uploaded, switch the Upload Material Component to the 'procesing' mode
						this.isProcessing = true;
					}
				}
				else if (event.type === HttpEventType.Response) {
					if (event.ok) {
						// the file is uploaded and processed;
						// Note: it seems the original author thinks this event completes the process. Maybe we don't need to check event.ok
						this.isProcessing = false;
					}

					// Refresh the material panel thumbnail
					this._orderHelperService.changeMaterialPanelReloadStatus(Math.random());

					// If the reporter is provided, report that upload is complete
					if (this._materialPreview && this._materialPreview.newMaterialUploadedReporter) {
						this._materialPreview.newMaterialUploadedReporter.next(new MaterialPreviewDialogResult());
						this._materialPreview.newMaterialUploadedReporter.complete();
					}

					// Load uploaded image preview
					this.loadPreview(materialId);
				}
			},
			error: (error: CassError) => {
				this.closeDialog();
				this._logger.error(this.COMPONENT_NAME, `Cannot upload material, orderId="${this.materialPreview?.orderId}", materialId="${materialId}"`);
				this._alertService.errorByCassError(error)
			}
		});
	}

	// seems not called by anyone
	//reset() {
	//	this.fileUploadProgress = undefined;
	//}

	openFindMaterialDialog($event: any) {
		this.closeDialog();

		this._matDialog.open(FindMaterialDialogComponent, {
			data: this.materialPreview,
			width: '80vw'
		});
	}

	reupload(): void {
		if (this._orderStateService.hasUnsavedChanges) {
			this._alertService.messageByTranslateKey("ORDER_BASKET.MATERIAL_COMMON.ORDER_UNSAVED_WARNING_TEXT", undefined, "ORDER_BASKET.MATERIAL_COMMON.ORDER_UNSAVED_WARNING_TITLE", undefined);
			return;
		}

		if (this._materialPreview.useExternalMaterialUploader) {
			const materialPreviewDialogResult = new MaterialPreviewDialogResult();
			materialPreviewDialogResult.externalMaterialUploaderRequested = true;

			this.closeDialog(materialPreviewDialogResult);
			//this._dialogRef.close(materialPreviewDialogResult);
		}
		else {
			this.imagePreviewUrl = '';
		}
	}

	downloadHires(): void {
		if (this.materialPreview && this.materialPreview.orderId && this.materialPreview.orderAdDetail && this.materialPreview.orderAdDetail.materialId) {
			this._orderService.getMaterialHires(this.materialPreview.orderId, this.materialPreview.orderAdDetail.materialId).subscribe({
				next: (response: Blob) => {
					const blob = new Blob([response], { type: 'application/pdf' });

					// Create a link element with the received blob, add it to the DOM, simulate a click to trigger download, and remove it
					const link = document.createElement('a');
					link.href = window.URL.createObjectURL(blob);
					link.download = `Order-${this.materialPreview.orderId}-material-${this.materialPreview.orderAdDetail.materialId}-hires.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				},
				error: (err: CassError) => {
					this._alertService.errorByCassError(err);
				}
			});
		}
	}
}
